import { useContext } from 'react';

import {
  selectRouteMetrics,
  selectScenarioRunUUID,
  selectScenarioSegments,
  useGetScenarioTravelTimeQuery,
} from 'state/detourSlices';
import { useAppSelector } from 'state/hooks';

import { ScenarioSetupUIMode } from '../../appConstants';
import { MetricsRouteData, XyArray } from '../../appTypes';
import { LayerContext } from '../../state/LayerContext';
import { useGetLayerQuery, useGetRouteInfoQuery } from '../../state/apiSlice';
import {
  selectProject,
  selectScenarioSetupUIMode,
} from '../../state/workflowSlice';
import {
  MINUTES_PER_DAY,
  PlanningChartDataExpander,
} from '../chart/PlanningChartDataExpander';
import { useRouteInfo } from '../common/hooks/useRouteInfo';
import { arrayEquals } from '../common/utils';
import { getRouteForSparseSegments } from '../map/utils/route_utils';
import {
  ClosureTimeslot,
  closureDayNames,
  closureTimeslotIndex,
} from './utils';

export function useScenarioTravelTimeDataProvider() {
  const project_slug = useAppSelector(selectProject);
  const currentScenarioUUID: string = useAppSelector(selectScenarioRunUUID);
  const scenarioSegments = useAppSelector(selectScenarioSegments);
  const uiMode = useAppSelector(selectScenarioSetupUIMode);
  const routeMetrics = useAppSelector(selectRouteMetrics);
  const { layer } = useContext(LayerContext);
  const { basicRouteInfo, feature: routeFeature } = useRouteInfo();
  // Get data for Route volumes

  const relevantMode = uiMode === ScenarioSetupUIMode.Scenarios && routeMetrics;

  const { currentData: layerData } = useGetLayerQuery(layer, {
    skip: !layer || !relevantMode,
  });

  let nonDetourSegments;
  if (
    basicRouteInfo.waypointSegments &&
    basicRouteInfo.waypointSegments.length >= 2 &&
    layerData?.routing
  ) {
    nonDetourSegments = getRouteForSparseSegments(layerData.routing, [
      basicRouteInfo.waypointSegments[0],
      ...scenarioSegments,
      basicRouteInfo.waypointSegments[
        basicRouteInfo.waypointSegments.length - 1
      ],
    ]);
    if (arrayEquals(nonDetourSegments, basicRouteInfo.realizedSegments)) {
      nonDetourSegments = undefined;
    }
  }

  const { currentData: nonDetourRouteInfo } = useGetRouteInfoQuery(
    {
      project_slug,
      segment_ids: nonDetourSegments,
      layer_id: layer,
    },
    { skip: !nonDetourSegments || !relevantMode },
  );

  const isNonDetourGood =
    !!nonDetourRouteInfo &&
    !!routeFeature &&
    nonDetourRouteInfo.feature.properties.route_length <
      routeFeature.properties.route_length * 2;

  const routeSegments = basicRouteInfo.realizedSegments;
  const { currentData: scenarioData, isLoading: scenarioLoading } =
    useGetScenarioTravelTimeQuery(
      { project_slug, uuid: currentScenarioUUID, routeIds: routeSegments },
      { skip: !routeSegments || !relevantMode },
    ) as any;

  const { currentData: nonDetourData, isLoading: nonDetourLoading } =
    useGetScenarioTravelTimeQuery(
      { project_slug, uuid: currentScenarioUUID, routeIds: nonDetourSegments },
      { skip: !nonDetourSegments || !relevantMode },
    ) as any;
  return {
    loading: scenarioLoading || nonDetourLoading,
    scenarioData,
    nonDetourData: isNonDetourGood ? nonDetourData : undefined,
    nonDetourSegments,
    nonDetourFeature: isNonDetourGood ? nonDetourRouteInfo?.feature : undefined,
  };
}

export class ScenarioTravelTimeChartDataExpander extends PlanningChartDataExpander {
  constructor(binSize: number | string, uses_middle_east_day_types: boolean) {
    super(binSize, uses_middle_east_day_types);
  }

  slotX(slot: ClosureTimeslot, timeslots: ClosureTimeslot[]) {
    const binTicks = this.binSize * 60 * 1000;
    const dayNum = closureDayNames(timeslots).indexOf(slot.dayName);
    const minutes = dayNum * MINUTES_PER_DAY + slot.minutesOfDay;
    return this.firstBin + (minutes / this.binSize) * binTicks;
  }

  closureChartXRange(timeslots: ClosureTimeslot[]) {
    const binTicks = this.binSize * 60 * 1000;
    const last = timeslots.at(-1);
    const prev = timeslots.at(-2);
    const endsAtClosureTime = prev && last.slotIndex === prev.slotIndex;
    return {
      min: this.slotX(timeslots[0], timeslots),
      max: endsAtClosureTime
        ? this.slotX(last, timeslots)
        : this.slotX(last, timeslots) + binTicks,
    };
  }

  expandTravelTimes(
    rawData: MetricsRouteData,
    data_offset: number,
    timeslots: ClosureTimeslot[],
  ): XyArray {
    if (!timeslots.length) {
      return [];
    }

    this.numBins = timeslots.length;

    const data = Array(this.numBins);
    for (let i = 0; i < this.numBins; i += 1) {
      data[i] = { x: this.slotX(timeslots[i], timeslots), y: null };
    }

    if (!rawData?.times) {
      return data;
    }

    const sparseData = rawData.times.map((row) => ({
      day: row[0],
      bin: row[1],
      y: row[data_offset],
    }));
    for (let i = 0; i < sparseData.length; i += 1) {
      const { day, bin, y } = sparseData[i];
      const index = closureTimeslotIndex(timeslots, day, bin);
      if (index >= 0 && index < data.length && y != null) {
        data[index].y = y;
      }
    }

    const last = timeslots.at(-1);
    const prev = timeslots.at(-2);
    const lastIndex = data.length - 1;
    // display data for the last bin on a chart - data will be prev (case 18:00-18:15) - same logic as in DetourTimeSlider
    if (prev && last.slotIndex === prev.slotIndex) {
      const prevIndex = lastIndex - 1;
      if (data[prevIndex].y != null) {
        data[lastIndex].y = data[prevIndex].y;
      }
    }

    return data;
  }
}
